import { z } from "zod";
import {
  REPORT_AGGS,
  REPORT_MAX_COLUMNS,
  REPORT_MAX_FILTERS,
  REPORT_MAX_GROUP,
  REPORT_MAX_IN_VALUES,
  REPORT_MAX_SORT,
  REPORT_MAX_TEXT,
  REPORT_OPS,
  REPORT_SOURCES,
  type ReportAgg,
  type ReportColumnType,
  type ReportDefinition,
  type ReportOp,
  type ReportPreset,
  type ReportSource,
} from "@cx/domain";

/**
 * Relatórios customizados (T3.6, RF-CAD-06). A definição é declarativa e só aceita chaves (fonte, coluna, operador,
 * resumo) — nunca SQL. O formato é conferido aqui; colunas, tipos e permissões, pelo compilador do domínio contra o
 * catálogo do DO.
 */
const colKey = z.string().regex(/^[a-z][a-z0-9_]{0,39}$/, "Coluna inválida");
const filterValue = z.union([z.string().max(REPORT_MAX_TEXT), z.number().int().safe(), z.boolean()]);
const agg = z.enum(REPORT_AGGS).nullish();

export const reportDefinitionInput = z.strictObject({
  source: z.enum(REPORT_SOURCES),
  columns: z
    .array(z.strictObject({ key: colKey, agg }))
    .min(1, "Escolha pelo menos uma coluna")
    .max(REPORT_MAX_COLUMNS),
  filters: z
    .array(
      z.strictObject({
        key: colKey,
        op: z.enum(REPORT_OPS),
        value: filterValue.nullish(),
        values: z.array(filterValue).max(REPORT_MAX_IN_VALUES).optional(),
      }),
    )
    .max(REPORT_MAX_FILTERS)
    .default([]),
  groupBy: z.array(colKey).max(REPORT_MAX_GROUP).default([]),
  sort: z
    .array(z.strictObject({ key: colKey, agg, dir: z.enum(["asc", "desc"]) }))
    .max(REPORT_MAX_SORT)
    .default([]),
}) satisfies z.ZodType<ReportDefinition, unknown>;
export type ReportDefinitionInput = z.infer<typeof reportDefinitionInput>;

const name = z.string().trim().min(1, "Informe o nome").max(80);
export const customReportInput = z.strictObject({
  name,
  description: z.string().trim().max(300).default(""),
  shared: z.boolean().default(false),
  definition: reportDefinitionInput,
});
export type CustomReportInput = z.infer<typeof customReportInput>;

export const customReportRunInput = z.strictObject({
  definition: reportDefinitionInput,
  offset: z.number().int().min(0).max(100_000).default(0),
});
export type CustomReportRunInput = z.infer<typeof customReportRunInput>;

export const REPORT_FILE_FORMATS = ["csv", "xlsx", "pdf"] as const;
export type ReportFileFormat = (typeof REPORT_FILE_FORMATS)[number];
export const customReportExportInput = z.strictObject({
  definition: reportDefinitionInput,
  /** Título do arquivo e do PDF. */
  name: z.string().trim().min(1).max(80).default("Relatório"),
  fileFormat: z.enum(REPORT_FILE_FORMATS).default("csv"),
});
export type CustomReportExportInput = z.infer<typeof customReportExportInput>;

/**
 * Permissões que o servidor calcula para quem roda (nunca vêm do corpo). Cada fonte exige a leitura do módulo dela;
 * `cost` = products:read_cost; `sellers` = sellers:read (vendedor e comissão).
 */
export type ReportGrants = {
  salesOrders: boolean;
  products: boolean;
  stock: boolean;
  contacts: boolean;
  receivables: boolean;
  payables: boolean;
  cost: boolean;
  sellers: boolean;
};

export type ReportResultColumn = {
  key: string;
  agg: ReportAgg | null;
  label: string;
  type: ReportColumnType;
  options?: Record<string, string>;
};
export type ReportResult = {
  columns: ReportResultColumn[];
  rows: (string | number | null)[][];
  /** Total de linhas, contado até 100.001 (`totalCapped` = "mais de 100.000"). */
  total: number;
  totalCapped: boolean;
  offset: number;
  limit: number;
};

export type ReportCatalogColumn = {
  key: string;
  label: string;
  type: ReportColumnType;
  options?: Record<string, string>;
  ops: ReportOp[];
  aggs: ReportAgg[];
};
export type ReportCatalogView = {
  sources: { key: ReportSource; label: string; columns: ReportCatalogColumn[] }[];
  /** Modelos prontos que o perfil consegue rodar. */
  presets: ReportPreset[];
};

export type CustomReportView = {
  id: string;
  name: string;
  description: string;
  source: ReportSource;
  shared: boolean;
  definition: ReportDefinition;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  /** Quem pode editar e arquivar (o autor, ou custom_reports:update no compartilhado). */
  canEdit: boolean;
};
